/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * X.509 trust evaluation for C2PA: builds the signing certificate chain to a
 * configured trust anchor (anchors.pem), verifying each link's signature with
 * WebCrypto and each certificate's validity. A signer is "trusted" when its
 * chain reaches an anchor and the leaf carries an allowed Extended Key Usage
 * (store.cfg). Mirrors the c2pa-rs trust handler's chain-to-anchor model.
 */

import { parseCertificate, type CertInfo } from './parseCert'
import { readTlv, readChildren, content, decodeOid } from './der'

export interface TrustStore {
    anchors: CertInfo[]
    /** Subject DER → anchor, for issuer lookup. */
    anchorsBySubject: Map<string, CertInfo>
    /** Allowed end-entity EKU OIDs (from store.cfg); empty = no EKU constraint. */
    allowedEku: Set<string>
    /** Base64 SHA-256 of explicitly-allowed end-entity certificates (allowed.sha256.txt). */
    allowedEndEntities: Set<string>
}

const hex = (b: Uint8Array): string => Array.from(b, x => x.toString(16).padStart(2, '0')).join('')

/** Parses a PEM bundle into DER certificates. */
export function parsePemCertificates (pem: string): Uint8Array[] {
    const out: Uint8Array[] = []
    const re = /-----BEGIN CERTIFICATE-----([\s\S]*?)-----END CERTIFICATE-----/g
    let m: RegExpExecArray | null
    while ((m = re.exec(pem)) !== null) {
        const b64 = m[1].replace(/\s+/g, '')
        const bin = atob(b64)
        const der = new Uint8Array(bin.length)
        for (let i = 0; i < bin.length; i++) der[i] = bin.charCodeAt(i)
        out.push(der)
    }
    return out
}

/** Parses store.cfg → set of allowed EKU OIDs (lines that look like dotted OIDs). */
export function parseStoreConfig (cfg: string): Set<string> {
    const out = new Set<string>()
    for (const line of cfg.split(/\r?\n/)) {
        const t = line.trim()
        if (/^\d+(\.\d+)+$/.test(t)) out.add(t)
    }
    return out
}

/** Parses allowed.sha256.txt → set of base64 SHA-256 end-entity hashes. */
export function parseAllowedList (txt: string): Set<string> {
    const out = new Set<string>()
    for (const line of txt.split(/\r?\n/)) {
        const t = line.trim()
        if (t && !t.startsWith('#')) out.add(t)
    }
    return out
}

/** Builds a TrustStore from the bridge's trust resources. */
export function buildTrustStore (anchorsPem: string, storeCfg?: string, allowedList?: string): TrustStore {
    const anchors = parsePemCertificates(anchorsPem).map(parseCertificate)
    const anchorsBySubject = new Map<string, CertInfo>()
    for (const a of anchors) anchorsBySubject.set(hex(a.subjectDer), a)
    return {
        anchors,
        anchorsBySubject,
        allowedEku: parseStoreConfig(storeCfg ?? ''),
        allowedEndEntities: parseAllowedList(allowedList ?? '')
    }
}

async function sha256Base64 (bytes: Uint8Array): Promise<string> {
    const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes as unknown as ArrayBuffer))
    let bin = ''
    for (const b of digest) bin += String.fromCharCode(b)
    return btoa(bin)
}

// --- certificate signature verification ---

interface SigAlg { importParams: RsaHashedImportParams | EcKeyImportParams | Algorithm, verifyParams: Algorithm | RsaPssParams | EcdsaParams, ecdsa: boolean }

function sigAlgForOid (oid: string): SigAlg | null {
    switch (oid) {
        case '1.2.840.113549.1.1.11': return { importParams: { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' }, verifyParams: { name: 'RSASSA-PKCS1-v1_5' }, ecdsa: false }
        case '1.2.840.113549.1.1.12': return { importParams: { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-384' }, verifyParams: { name: 'RSASSA-PKCS1-v1_5' }, ecdsa: false }
        case '1.2.840.113549.1.1.13': return { importParams: { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-512' }, verifyParams: { name: 'RSASSA-PKCS1-v1_5' }, ecdsa: false }
        case '1.2.840.10045.4.3.2': return { importParams: { name: 'ECDSA', namedCurve: 'P-256' }, verifyParams: { name: 'ECDSA', hash: 'SHA-256' }, ecdsa: true }
        case '1.2.840.10045.4.3.3': return { importParams: { name: 'ECDSA', namedCurve: 'P-384' }, verifyParams: { name: 'ECDSA', hash: 'SHA-384' }, ecdsa: true }
        case '1.2.840.10045.4.3.4': return { importParams: { name: 'ECDSA', namedCurve: 'P-521' }, verifyParams: { name: 'ECDSA', hash: 'SHA-512' }, ecdsa: true }
        // Ed25519 (COSE-4): id-Ed25519 signature. Raw 64-byte sig (no DER→raw). Uneven
        // runtime support — where crypto.subtle lacks it, importKey/verify throws →
        // caught below → false (fail-closed false-reject, not a bypass).
        case '1.3.101.112': return { importParams: { name: 'Ed25519' }, verifyParams: { name: 'Ed25519' }, ecdsa: false }
        // RSASSA-PSS (1.2.840.113549.1.1.10) carries params; not used by the fixtures' chains.
        default: return null
    }
}

/**
 * Converts a DER-encoded ECDSA signature (SEQUENCE{r,s}) to the raw r||s WebCrypto
 * expects. Parses the SEQUENCE and INTEGER lengths properly (short + long form) so
 * a P-521 signature (~134-byte content → long-form SEQUENCE length) is read correctly.
 */
export function ecdsaDerToRaw (der: Uint8Array, size: number): Uint8Array {
    const seq = readTlv(der, 0) // SEQUENCE { INTEGER r, INTEGER s }
    let p = seq.contentStart
    const read = (): Uint8Array => {
        const t = readTlv(der, p) // INTEGER
        let v = der.subarray(t.contentStart, t.contentEnd)
        p = t.end
        while (v.length > size && v[0] === 0) v = v.subarray(1) // strip sign padding
        const out = new Uint8Array(size); out.set(v, size - v.length)
        return out
    }
    const r = read(); const s = read()
    const out = new Uint8Array(size * 2); out.set(r, 0); out.set(s, size)
    return out
}

const ecdsaSize: Record<string, number> = { 'P-256': 32, 'P-384': 48, 'P-521': 66 }

const SHA_OID_TO_NAME: Record<string, string> = {
    '2.16.840.1.101.3.4.2.1': 'SHA-256',
    '2.16.840.1.101.3.4.2.2': 'SHA-384',
    '2.16.840.1.101.3.4.2.3': 'SHA-512'
}

/** Extracts the hash + saltLength from an RSASSA-PSS AlgorithmIdentifier's parameters. */
function pssParams (params: Uint8Array | undefined): { hash: string, saltLength: number } | null {
    if (!params) return null
    try {
        const seq = readTlv(params, 0)
        const items = readChildren(params, seq.contentStart, seq.contentEnd)
        const hashWrap = items.find(t => t.tag === 0xa0) // [0] hashAlgorithm (default sha1 → we require SHA-2)
        if (!hashWrap) return null
        const hashAlg = readTlv(params, hashWrap.contentStart)
        const hash = SHA_OID_TO_NAME[decodeOid(content(params, readChildren(params, hashAlg.contentStart, hashAlg.contentEnd)[0]))]
        if (!hash) return null
        const saltWrap = items.find(t => t.tag === 0xa2) // [2] saltLength INTEGER (default 20)
        const saltLength = saltWrap ? content(params, readTlv(params, saltWrap.contentStart))[0] : 20
        return { hash, saltLength }
    } catch {
        return null
    }
}

/**
 * Verifies a DER signature (`signature`) over `tbs` using the public key in
 * `spki` (a SubjectPublicKeyInfo), interpreting the X.509 signatureAlgorithm
 * `sigAlgOid` (+ `sigAlgParams` for RSASSA-PSS). Handles RSA-PKCS1-v1_5,
 * ECDSA (P-256/384/521, DER→raw), and RSASSA-PSS. Shared by cert-chain
 * verification and RFC 6960 OCSP-response verification.
 */
export async function verifySignatureWithSpki (spki: Uint8Array, sigAlgOid: string, sigAlgParams: Uint8Array | undefined, signature: Uint8Array, tbs: Uint8Array): Promise<boolean> {
    try {
        // RSASSA-PSS (§13.2 PS256/384/512): the hash + salt live in the sigAlg params.
        if (sigAlgOid === '1.2.840.113549.1.1.10') {
            const pss = pssParams(sigAlgParams)
            if (!pss) return false
            const key = await crypto.subtle.importKey('spki', spki as unknown as ArrayBuffer, { name: 'RSA-PSS', hash: pss.hash }, false, ['verify'])
            return await crypto.subtle.verify({ name: 'RSA-PSS', saltLength: pss.saltLength }, key, signature as unknown as ArrayBuffer, tbs as unknown as ArrayBuffer)
        }
        const spec = sigAlgForOid(sigAlgOid)
        if (!spec) return false
        const key = await crypto.subtle.importKey('spki', spki as unknown as ArrayBuffer, spec.importParams, false, ['verify'])
        let sig = signature
        if (spec.ecdsa) sig = ecdsaDerToRaw(sig, ecdsaSize[(spec.importParams as EcKeyImportParams).namedCurve] ?? 32)
        return await crypto.subtle.verify(spec.verifyParams, key, sig as unknown as ArrayBuffer, tbs as unknown as ArrayBuffer)
    } catch {
        return false
    }
}

/** Verifies that `cert` was signed by `issuer`'s public key. */
export async function verifyCertSignedBy (cert: CertInfo, issuer: CertInfo): Promise<boolean> {
    return verifySignatureWithSpki(issuer.spki, cert.signatureAlgOid, cert.sigAlgParams, cert.signature, cert.tbs)
}

function valid (cert: CertInfo, atIso: string): boolean {
    return cert.notBefore <= atIso && atIso <= cert.notAfter
}

export interface TrustResult {
    trusted: boolean
    /** A reason code when not trusted, for diagnostics. */
    reason?: string
}

/**
 * Determines whether a signing certificate chain is trusted: builds the path
 * from the leaf (using the supplied intermediates) to a trust anchor, verifying
 * each issued-by signature and certificate validity at `atIso`. The leaf must
 * also carry an allowed EKU when the store constrains them.
 */
export async function evaluateTrust (chainDer: Uint8Array[], store: TrustStore, atIso: string): Promise<TrustResult> {
    if (chainDer.length === 0) return { trusted: false, reason: 'no chain' }
    let chain: CertInfo[]
    try {
        chain = chainDer.map(parseCertificate)
    } catch {
        return { trusted: false, reason: 'parse' }
    }

    const leaf = chain[0]

    // Allowed end-entity: trusted directly by certificate hash alone — ahead of
    // any EKU/validity/chain evaluation and with no requirement that the issuer
    // be presented. Mirrors c2pa-rs CertificateTrustPolicy::check_certificate_trust,
    // which returns TrustAnchorType::EndEntity on a hash match before delegating
    // to the chain walk (C2PA §14.4.3: a private-credential-store signer is
    // trusted directly and never acts as an issuer). Certificate expiry is still
    // enforced by the separate signing-cert validity check in the verify pipeline.
    if (store.allowedEndEntities.size > 0) {
        const leafHash = await sha256Base64(chainDer[0])
        if (store.allowedEndEntities.has(leafHash)) return { trusted: true }
    }

    // Chain-to-anchor path: an allowed EKU and validity at the reference time are
    // required before walking to an anchor (mirrors rust_native check_certificate_trust,
    // which returns InvalidEku when the EKU extension is absent). A leaf with NO EKU —
    // notably a CA certificate, which typically carries none — must NOT skip the gate:
    // otherwise an EKU-less/CA cert chaining to an anchor is trusted (§14.5.1.1 l.2477:
    // the EKU extension shall be present and non-empty in an end-entity cert; l.2481:
    // a time-stamping cert shall be valid for id-kp-timeStamping). This is the load-bearing
    // constraint on the TSA path, where the leaf receives no separate profile check.
    if (store.allowedEku.size > 0 && !leaf.eku.some(e => store.allowedEku.has(e))) {
        return { trusted: false, reason: 'eku' }
    }
    if (!valid(leaf, atIso)) return { trusted: false, reason: 'expired' }

    const intermediatesBySubject = new Map<string, CertInfo>()
    for (const c of chain.slice(1)) intermediatesBySubject.set(hex(c.subjectDer), c)

    // Walk issuer links from the leaf until we reach (and verify into) an anchor.
    // RFC 5280 §6 (§14.5.1.2): every intermediate CA used as an issuer must assert
    // basicConstraints cA=TRUE + keyUsage.keyCertSign, and its pathLenConstraint
    // bounds the number of non-self-issued CA certs below it. The trust anchor is a
    // trust input (not constrained here); the leaf is the end-entity (§14.5.1.1).
    let current = leaf
    const seen = new Set<string>()
    let intermediateCount = 0 // non-self-issued intermediate CAs traversed below `current`
    for (let depth = 0; depth < 10; depth++) {
        if (!valid(current, atIso)) return { trusted: false, reason: 'expired-in-chain' }
        const issuerKey = hex(current.issuerDer)
        const anchor = store.anchorsBySubject.get(issuerKey)
        if (anchor) {
            return (await verifyCertSignedBy(current, anchor)) ? { trusted: true } : { trusted: false, reason: 'anchor-sig' }
        }
        // Self-issued cert whose subject is itself an anchor. This is redundant with
        // the anchor branch above (which verifies `current`'s signature against that
        // same anchor and returns first for any self-issued cert matching an anchor),
        // so it is effectively unreachable — but if the branch above ever changes,
        // verify the signature here too rather than granting name-only trust (X509-5).
        if (hex(current.subjectDer) === issuerKey) {
            const selfAnchor = store.anchorsBySubject.get(hex(current.subjectDer))
            if (selfAnchor) return (await verifyCertSignedBy(current, selfAnchor)) ? { trusted: true } : { trusted: false, reason: 'self-anchor-sig' }
        }
        const issuer = intermediatesBySubject.get(issuerKey)
        if (!issuer || seen.has(issuerKey)) return { trusted: false, reason: 'no-path-to-anchor' }
        // RFC 5280 §6.1.4: a non-leaf issuer must be a CA authorized to sign certs.
        if (!issuer.isCa || !(issuer.keyUsage?.keyCertSign ?? false)) return { trusted: false, reason: 'issuer-not-ca' }
        // pathLenConstraint: at most `pathLen` CA certs may sit between this issuer and the leaf.
        if (issuer.pathLen !== undefined && intermediateCount > issuer.pathLen) return { trusted: false, reason: 'pathlen' }
        if (!(await verifyCertSignedBy(current, issuer))) return { trusted: false, reason: 'chain-sig' }
        seen.add(issuerKey)
        if (hex(issuer.subjectDer) !== hex(issuer.issuerDer)) intermediateCount++ // self-issued CAs don't count
        current = issuer
    }
    return { trusted: false, reason: 'too-deep' }
}
